import React, { useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import { useHistory } from "react-router-dom";
import { Button, Alert, Form, Dropdown } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSync,
  faEnvelope,
  faSave,
  faExclamationTriangle,
  faEdit,
  faTimes,
  faPaperPlane,
  faUpload,
  faTrash,
  faCaretDown,
} from "@fortawesome/free-solid-svg-icons";

import { PAGES } from "../../constants/pages";
import { RESPONSE_CODE } from "../../constants/errors";
import {
  get_email_templates,
  save_draft_email_template,
  publish_email_template,
  discard_draft_email_template,
  get_brevo_templates,
  refresh_brevo_templates,
  send_test_email_template,
} from "../../actions/emailTemplateAction";
import GenericModal from "../global/GenericModal/GenericModal";
import TemplateStatusBadge from "./TemplateStatusBadge";
import { utils_validation } from "../../utils/utils_validation";

function TestButton({
  code,
  brevoTemplateName,
  variant,
  label,
  testingKey,
  testEmail,
  isLoading,
  onTest,
  className,
}) {
  const emailValid = utils_validation.isEmail(testEmail);
  const canTest = emailValid && !!brevoTemplateName;
  const hint = !testEmail
    ? "Enter a test recipient email above"
    : !emailValid
      ? "Enter a valid email address"
      : !brevoTemplateName
        ? "No template assigned"
        : undefined;

  return (
    <Button
      variant={variant}
      size="sm"
      className={className}
      title={hint}
      style={
        !canTest || isLoading
          ? { opacity: 0.65, cursor: "not-allowed" }
          : undefined
      }
      disabled={!canTest || isLoading}
      onClick={() => {
        if (!canTest || isLoading) return;
        onTest(code, brevoTemplateName, testingKey);
      }}
    >
      <FontAwesomeIcon icon={faPaperPlane} className="mr-1" />
      {isLoading ? "..." : label}
    </Button>
  );
}

const BrevoInputToggle = React.forwardRef(
  ({ value, onValueChange, onOpen, onClick, placeholder }, ref) => (
    <div ref={ref} className="input-group input-group-focus-within">
      <Form.Control
        value={value}
        onChange={(e) => {
          onValueChange(e);
          onOpen();
        }}
        placeholder={placeholder}
        autoComplete="off"
      />
      <div className="input-group-append">
        <Button variant="outline-secondary" onClick={onClick} tabIndex={-1}>
          <FontAwesomeIcon icon={faCaretDown} />
        </Button>
      </div>
    </div>
  ),
);

function BrevoTemplateSelector({
  id,
  value,
  options,
  isOpen,
  onToggle,
  onChange,
}) {
  const query = value.toLowerCase();
  const filtered = query
    ? options.filter((bt) => bt.name.toLowerCase().includes(query))
    : options;
  return (
    <Dropdown show={isOpen} onToggle={onToggle}>
      <Dropdown.Toggle
        as={BrevoInputToggle}
        id={id}
        value={value}
        onValueChange={(e) => onChange(e.target.value)}
        onOpen={() => onToggle(true)}
        placeholder="Enter Brevo template name"
      />
      <Dropdown.Menu
        style={{ maxHeight: "200px", overflowY: "auto", width: "100%" }}
      >
        <Dropdown.Item onClick={() => onChange("")}>
          <span className="text-muted">-- Not assigned --</span>
        </Dropdown.Item>
        {filtered.map((bt) => (
          <Dropdown.Item key={bt.id} onClick={() => onChange(bt.name)}>
            {bt.name}
          </Dropdown.Item>
        ))}
      </Dropdown.Menu>
    </Dropdown>
  );
}

function EmailTemplates() {
  const dispatch = useDispatch();
  const history = useHistory();

  const [templates, setTemplates] = useState([]);
  const [brevoTemplates, setBrevoTemplates] = useState([]);
  const [editedRows, setEditedRows] = useState({});
  const [editingRows, setEditingRows] = useState({});
  const [saving, setSaving] = useState({});
  const [publishing, setPublishing] = useState({});
  const [discarding, setDiscarding] = useState({});
  const [confirmingPublish, setConfirmingPublish] = useState(null);
  const [openDropdown, setOpenDropdown] = useState(null);
  const [error, setError] = useState("");
  const [warning, setWarning] = useState("");
  const [success, setSuccess] = useState("");
  const [syncing, setSyncing] = useState(false);
  const [showBrevoList, setShowBrevoList] = useState(false);
  const [testEmail, setTestEmail] = useState("");
  const [testing, setTesting] = useState({});

  useEffect(() => {
    dispatch(get_email_templates())
      .then((data) => {
        if (data) {
          setTemplates(data);
        }
        setEditedRows({});
      })
      .catch((err) => {
        if (err.response?.status === RESPONSE_CODE["401_unauthorized"]) {
          history.push(PAGES.LOGIN);
        } else {
          setError("Failed to load email templates");
        }
      });
    dispatch(get_brevo_templates())
      .then((data) => {
        if (data)
          setBrevoTemplates(
            [...data].sort((a, b) => a.name.localeCompare(b.name)),
          );
      })
      .catch((err) => {
        if (err.response?.status === RESPONSE_CODE["401_unauthorized"]) {
          history.push(PAGES.LOGIN);
        } else {
          setError("Failed to load brevo templates from cache");
        }
      });
  }, [dispatch, history]);

  function loadBrevoTemplates() {
    return dispatch(get_brevo_templates())
      .then((data) => {
        if (data)
          setBrevoTemplates(
            [...data].sort((a, b) => a.name.localeCompare(b.name)),
          );
      })
      .catch((err) => {
        if (err.response?.status === RESPONSE_CODE["401_unauthorized"]) {
          history.push(PAGES.LOGIN);
        } else {
          setError("Failed to load brevo templates from cache");
        }
      });
  }

  function handleSyncBrevo() {
    setSyncing(true);
    setError("");
    dispatch(refresh_brevo_templates())
      .then(() => loadBrevoTemplates())
      .then(() => {
        setSyncing(false);
      })
      .catch((err) => {
        setSyncing(false);
        if (err.response?.status === RESPONSE_CODE["401_unauthorized"]) {
          history.push(PAGES.LOGIN);
        } else {
          setError("Failed to refresh Brevo templates");
        }
      });
  }

  function handleChange(code, value) {
    setEditedRows((prev) => ({ ...prev, [code]: value }));
    setSuccess("");
  }

  function handleSave(code) {
    const draft_brevo_template_name = editedRows[code];
    if (draft_brevo_template_name === undefined) return;

    setSaving((prev) => ({ ...prev, [code]: true }));
    setError("");
    setSuccess("");

    dispatch(save_draft_email_template({ code, draft_brevo_template_name }))
      .then((updated) => {
        setTemplates((prev) =>
          prev.map((t) => (t.code === code ? updated : t)),
        );
        setEditedRows((prev) => {
          const next = { ...prev };
          delete next[code];
          return next;
        });
        setSaving((prev) => ({ ...prev, [code]: false }));
        setEditingRows((prev) => {
          const next = { ...prev };
          delete next[code];
          return next;
        });
        setOpenDropdown(null);
        setSuccess(`Draft saved for ${code}`);
      })
      .catch((err) => {
        setSaving((prev) => ({ ...prev, [code]: false }));
        if (err.response?.status === RESPONSE_CODE["401_unauthorized"]) {
          history.push(PAGES.LOGIN);
        } else {
          setError(`Failed to save draft for ${code}`);
        }
      });
  }

  function handlePublishConfirm() {
    const code = confirmingPublish;
    setPublishing((prev) => ({ ...prev, [code]: true }));
    setError("");
    setSuccess("");

    dispatch(publish_email_template({ code }))
      .then((updated) => {
        setTemplates((prev) =>
          prev.map((t) => (t.code === code ? updated : t)),
        );
        setPublishing((prev) => ({ ...prev, [code]: false }));
        setConfirmingPublish(null);
        setSuccess(`Published ${code}`);
        const liveValue = updated.brevo_template_name;
        setWarning(
          brevoTemplates.some((bt) => bt.name === liveValue)
            ? ""
            : `"${liveValue}" is not a known Brevo template`,
        );
      })
      .catch((err) => {
        setPublishing((prev) => ({ ...prev, [code]: false }));
        setConfirmingPublish(null);
        if (err.response?.status === RESPONSE_CODE["401_unauthorized"]) {
          history.push(PAGES.LOGIN);
        } else {
          setError(`Failed to publish ${code}`);
        }
      });
  }

  function handleDiscardDraft(code) {
    setDiscarding((prev) => ({ ...prev, [code]: true }));
    setError("");
    setSuccess("");

    dispatch(discard_draft_email_template({ code }))
      .then((updated) => {
        setTemplates((prev) =>
          prev.map((t) => (t.code === code ? updated : t)),
        );
        setDiscarding((prev) => ({ ...prev, [code]: false }));
        setSuccess(`Draft discarded for ${code}`);
      })
      .catch((err) => {
        setDiscarding((prev) => ({ ...prev, [code]: false }));
        if (err.response?.status === RESPONSE_CODE["401_unauthorized"]) {
          history.push(PAGES.LOGIN);
        } else {
          setError(`Failed to discard draft for ${code}`);
        }
      });
  }

  function getDraftDisplayValue(template) {
    const edited = editedRows[template.code];
    return edited !== undefined
      ? edited
      : template.draft_brevo_template_name || "";
  }

  function handleTest(code, brevo_template_name, testingKey) {
    if (!testEmail || !utils_validation.isEmail(testEmail)) {
      setError("Enter a valid test recipient email above");
      return;
    }

    setTesting((prev) => ({ ...prev, [testingKey]: true }));
    setError("");
    setSuccess("");

    dispatch(
      send_test_email_template({ to: testEmail, code, brevo_template_name }),
    )
      .then(() => {
        setTesting((prev) => ({ ...prev, [testingKey]: false }));
        setSuccess(`Sent test for ${code} to ${testEmail}`);
      })
      .catch((err) => {
        setTesting((prev) => ({ ...prev, [testingKey]: false }));
        if (err.response?.status === RESPONSE_CODE["401_unauthorized"]) {
          history.push(PAGES.LOGIN);
        } else {
          setError(
            err.response?.data?.message || `Failed to send test for ${code}`,
          );
        }
      });
  }

  const confirmingTemplate = confirmingPublish
    ? templates.find((t) => t.code === confirmingPublish)
    : null;

  const testEmailValid = utils_validation.isEmail(testEmail);

  return (
    <section className="container-fluid">
      <div className="mt-3 mb-3">
        <div className="mb-3 text-muted text-gray-800">
          <span className="h3">Email Templates</span>
        </div>

        {error && (
          <Alert variant="danger" dismissible onClose={() => setError("")}>
            {error}
          </Alert>
        )}
        {warning && (
          <Alert variant="warning" dismissible onClose={() => setWarning("")}>
            {warning}
          </Alert>
        )}
        <div className="card mb-4">
          <div className="card-header py-3 d-flex justify-content-between align-items-center">
            <p className="m-0 text-gray-600">
              <FontAwesomeIcon icon={faEnvelope} className="mr-2" />
              {templates.length} system emails
              {success && (
                <small className="ml-2 text-success">{success}</small>
              )}
            </p>
            <div>
              <Button
                variant="outline-success"
                size="sm"
                onClick={handleSyncBrevo}
                disabled={syncing}
              >
                <FontAwesomeIcon icon={faSync} className="mr-1" />
                {syncing ? "Syncing..." : "Sync Brevo Templates"}
              </Button>
              {brevoTemplates.length > 0 && (
                <small
                  className="ml-2 text-success"
                  style={{ cursor: "pointer", textDecoration: "underline" }}
                  onClick={() => setShowBrevoList((prev) => !prev)}
                >
                  {brevoTemplates.length} templates loaded
                </small>
              )}
            </div>
          </div>
          {showBrevoList && (
            <div className="card-body border-bottom py-2">
              <Form.Control
                as="textarea"
                readOnly
                style={{
                  height: "150px",
                  resize: "vertical",
                  fontSize: "0.85rem",
                }}
                value={brevoTemplates.map((bt) => bt.name).join("\n")}
              />
            </div>
          )}
          <div className="card-body border-bottom py-2">
            <Form.Group className="mb-0">
              <Form.Label className="text-muted small mb-1">
                Test recipient email
              </Form.Label>
              <Form.Control
                type="email"
                size="sm"
                placeholder="you@example.com"
                value={testEmail}
                onChange={(e) => setTestEmail(e.target.value)}
                isInvalid={!!testEmail && !testEmailValid}
                isValid={!!testEmail && testEmailValid}
              />
              <Form.Control.Feedback type="invalid">
                Enter a valid email address
              </Form.Control.Feedback>
            </Form.Group>
          </div>
          <div className="card-body">
            <div className="table-responsive">
              <table className="table" width="100%">
                <thead>
                  <tr>
                    <th style={{ whiteSpace: "nowrap", width: "1%" }}>
                      System Email Name
                    </th>
                    <th>Currently Live</th>
                    <th>Draft (pending)</th>
                    <th style={{ width: "90px" }}>Status</th>
                    <th style={{ width: "1%" }}></th>
                  </tr>
                </thead>
                <tbody>
                  {templates.map((template) => (
                    <tr key={template.code}>
                      <td className="align-middle">{template.code}</td>
                      <td className="align-middle">
                        <div className="d-flex align-items-center">
                          <span>
                            {template.brevo_template_name ? (
                              template.brevo_template_name
                            ) : (
                              <span className="text-muted">
                                -- Not assigned --
                              </span>
                            )}
                          </span>
                          {brevoTemplates.length > 0 &&
                            template.brevo_template_name &&
                            !brevoTemplates.some(
                              (bt) => bt.name === template.brevo_template_name,
                            ) && (
                              <FontAwesomeIcon
                                icon={faExclamationTriangle}
                                className="ml-2 text-warning"
                                title="Not a known Brevo template"
                              />
                            )}
                        </div>
                      </td>
                      <td className="align-middle">
                        {editingRows[template.code] ? (
                          <BrevoTemplateSelector
                            id={`brevo-dropdown-${template.code}`}
                            value={getDraftDisplayValue(template)}
                            options={brevoTemplates}
                            isOpen={openDropdown === template.code}
                            onToggle={(isOpen) =>
                              setOpenDropdown(isOpen ? template.code : null)
                            }
                            onChange={(value) =>
                              handleChange(template.code, value)
                            }
                          />
                        ) : (
                          <span>
                            {template.draft_brevo_template_name ? (
                              template.draft_brevo_template_name
                            ) : (
                              <span className="text-muted">
                                (no pending change)
                              </span>
                            )}
                          </span>
                        )}
                      </td>
                      <td className="align-middle text-center">
                        <TemplateStatusBadge
                          liveTemplate={template.brevo_template_name}
                          draftTemplate={template.draft_brevo_template_name}
                        />
                      </td>
                      <td
                        className="align-middle text-right"
                        style={{ whiteSpace: "nowrap" }}
                      >
                        {editingRows[template.code] ? (
                          <>
                            <Button
                              variant="primary"
                              size="sm"
                              className="mr-1"
                              onClick={() => handleSave(template.code)}
                              disabled={
                                editedRows[template.code] === undefined ||
                                saving[template.code]
                              }
                            >
                              <FontAwesomeIcon icon={faSave} className="mr-1" />
                              {saving[template.code] ? "..." : "Save Draft"}
                            </Button>
                            <Button
                              variant="outline-secondary"
                              size="sm"
                              title="Cancel"
                              onClick={() => {
                                setOpenDropdown(null);
                                setEditingRows((prev) => {
                                  const next = { ...prev };
                                  delete next[template.code];
                                  return next;
                                });
                                setEditedRows((prev) => {
                                  const next = { ...prev };
                                  delete next[template.code];
                                  return next;
                                });
                              }}
                            >
                              <FontAwesomeIcon icon={faTimes} />
                            </Button>
                          </>
                        ) : (
                          <>
                            <Button
                              variant="outline-primary"
                              size="sm"
                              className="mr-1"
                              title="Edit draft"
                              onClick={() =>
                                setEditingRows((prev) => ({
                                  ...prev,
                                  [template.code]: true,
                                }))
                              }
                            >
                              <FontAwesomeIcon icon={faEdit} />
                            </Button>
                            {template.draft_brevo_template_name && (
                              <>
                                <Button
                                  variant="success"
                                  size="sm"
                                  className="mr-1"
                                  title="Publish draft to live"
                                  onClick={() =>
                                    setConfirmingPublish(template.code)
                                  }
                                  disabled={publishing[template.code]}
                                >
                                  <FontAwesomeIcon
                                    icon={faUpload}
                                    className="mr-1"
                                  />
                                  Publish
                                </Button>
                                <Button
                                  variant="outline-danger"
                                  size="sm"
                                  className="mr-1"
                                  title="Discard draft"
                                  onClick={() =>
                                    handleDiscardDraft(template.code)
                                  }
                                  disabled={discarding[template.code]}
                                >
                                  <FontAwesomeIcon icon={faTrash} />
                                </Button>
                              </>
                            )}
                            <TestButton
                              code={template.code}
                              brevoTemplateName={template.brevo_template_name}
                              variant="outline-success"
                              label="Test Live"
                              testingKey={`${template.code}_live`}
                              testEmail={testEmail}
                              isLoading={testing[`${template.code}_live`]}
                              onTest={handleTest}
                              className="mr-1"
                            />
                            {template.draft_brevo_template_name && (
                              <TestButton
                                code={template.code}
                                brevoTemplateName={
                                  template.draft_brevo_template_name
                                }
                                variant="outline-secondary"
                                label="Test Draft"
                                testingKey={`${template.code}_draft`}
                                testEmail={testEmail}
                                isLoading={testing[`${template.code}_draft`]}
                                onTest={handleTest}
                              />
                            )}
                          </>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      <GenericModal
        show={!!confirmingPublish}
        title="Publish email template"
        canProceed={!publishing[confirmingPublish]}
        proceeding={publishing[confirmingPublish]}
        onCancel={() => setConfirmingPublish(null)}
        onContinue={handlePublishConfirm}
      >
        {confirmingTemplate && (
          <div>
            <p className="mb-1">
              <strong>Template:</strong> {confirmingTemplate.code}
            </p>
            <p className="mb-3 text-muted small">
              This change will take effect immediately in the current
              environment.
            </p>
            <p className="mb-1">
              <strong>Brevo template name will change:</strong>
            </p>
            <table className="table table-sm table-borderless mb-0">
              <tbody>
                <tr>
                  <td className="text-muted pl-0" style={{ width: "50px" }}>
                    from
                  </td>
                  <td>
                    {confirmingTemplate.brevo_template_name ? (
                      <code>{confirmingTemplate.brevo_template_name}</code>
                    ) : (
                      <span className="text-muted">-- Not assigned --</span>
                    )}
                  </td>
                </tr>
                <tr>
                  <td className="text-muted pl-0">to</td>
                  <td>
                    <code>{confirmingTemplate.draft_brevo_template_name}</code>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        )}
      </GenericModal>
    </section>
  );
}

export default EmailTemplates;
